import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
  getDomainHealth,
  getServerLabel,
  type IDomainHealth
} from '../../lib/health';
import type { TDomainConfig, TPublicDomain } from '../../shared';

interface IDomainListProps {
  domains: TPublicDomain[];
  isBusy: boolean;
  config: TDomainConfig;
  pendingUpdateIds: ReadonlySet<string>;
  pendingDeleteIds: ReadonlySet<string>;
  onUpdateSettings: (
    domainId: string,
    targetIp: string,
    proxied: boolean
  ) => Promise<void>;
  onDelete: (domainId: string) => Promise<void>;
}

interface IDomainRowProps {
  domain: TPublicDomain;
  createdAtLabel: string;
  health: IDomainHealth;
  isUpdating: boolean;
  isDeleting: boolean;
  isBusy: boolean;
  onUpdateSettings: (
    domainId: string,
    targetIp: string,
    proxied: boolean
  ) => Promise<void>;
  onDelete: (domainId: string) => Promise<void>;
}

const DomainRowComponent = ({
  domain,
  createdAtLabel,
  health,
  isUpdating,
  isDeleting,
  isBusy,
  onUpdateSettings,
  onDelete
}: IDomainRowProps) => {
  const [targetIpInput, setTargetIpInput] = useState(domain.targetIp);
  const [proxiedInput, setProxiedInput] = useState(domain.proxied);

  useEffect(() => {
    setTargetIpInput(domain.targetIp);
    setProxiedInput(domain.proxied);
  }, [domain.proxied, domain.targetIp]);

  const serverLabel = useMemo(
    () => getServerLabel(domain.serverInfo),
    [domain.serverInfo]
  );

  const hasChanges = useMemo(() => {
    return (
      targetIpInput.trim() !== domain.targetIp ||
      proxiedInput !== domain.proxied
    );
  }, [domain.proxied, domain.targetIp, proxiedInput, targetIpInput]);

  const handleSave = useCallback(async () => {
    await onUpdateSettings(domain.id, targetIpInput.trim(), proxiedInput);
  }, [domain.id, onUpdateSettings, proxiedInput, targetIpInput]);

  const handleDelete = useCallback(async () => {
    const isConfirmed = window.confirm(
      `Delete ${domain.fqdn}? This removes its DNS record immediately and frees the name for anyone else to claim.`
    );
    if (!isConfirmed) return;

    await onDelete(domain.id);
  }, [domain.fqdn, domain.id, onDelete]);

  return (
    <article className="domain-row">
      <div>
        <p className="font-mono text-sm font-semibold text-foreground">
          {domain.fqdn}
        </p>
        <p
          className={
            health.state === 'down'
              ? 'mt-1 text-xs text-destructive'
              : 'mt-1 text-xs text-muted-foreground'
          }
        >
          {health.label}
          {health.detail ? ` · ${health.detail}` : ''}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Added {createdAtLabel}
        </p>
        {serverLabel ? (
          <p className="mt-1 text-xs text-muted-foreground">{serverLabel}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          className="form-input h-10 w-full sm:w-44"
          disabled={isBusy || isUpdating || isDeleting}
          onChange={(event) => setTargetIpInput(event.target.value)}
          placeholder="203.0.113.10"
          value={targetIpInput}
        />

        <label className="flex items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-xs text-muted-foreground">
          <input
            checked={proxiedInput}
            disabled={isBusy || isUpdating || isDeleting}
            onChange={(event) => setProxiedInput(event.target.checked)}
            type="checkbox"
          />
          Proxy
        </label>

        <button
          className="secondary-button h-10 px-3 text-xs"
          disabled={isBusy || isUpdating || isDeleting || !hasChanges}
          onClick={() => void handleSave()}
          type="button"
        >
          {isUpdating ? 'Saving...' : 'Save'}
        </button>

        <button
          className="danger-button"
          disabled={isBusy || isDeleting || isUpdating}
          onClick={() => void handleDelete()}
          type="button"
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </article>
  );
};

const DomainRow = memo(DomainRowComponent);

const DomainListComponent = ({
  domains,
  isBusy,
  config,
  pendingUpdateIds,
  pendingDeleteIds,
  onUpdateSettings,
  onDelete
}: IDomainListProps) => {
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
    []
  );

  const rows = useMemo(() => {
    return [...domains]
      .sort((firstDomain, secondDomain) =>
        secondDomain.createdAt.localeCompare(firstDomain.createdAt)
      )
      .map((domain) => ({
        domain,
        health: getDomainHealth({
          lastSeenUpAt: domain.lastSeenUpAt,
          graceDays: config.healthCheckGraceDays,
          intervalMinutes: config.healthCheckIntervalMinutes
        })
      }));
  }, [config.healthCheckGraceDays, config.healthCheckIntervalMinutes, domains]);

  // Read from config rather than hardcoded, so the warning cannot drift from
  // the grace period the job actually enforces.
  const gracePeriodLabel = useMemo(() => {
    const days = config.healthCheckGraceDays;
    return days === 1 ? '1 day' : `${days} days`;
  }, [config.healthCheckGraceDays]);

  if (rows.length === 0) {
    return (
      <section className="surface-panel">
        <h2 className="text-lg font-semibold text-foreground">Your domains</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          No domains claimed yet. Claim your first one above.
        </p>
      </section>
    );
  }

  return (
    <section className="surface-panel">
      <h2 className="text-lg font-semibold text-foreground">Your domains</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Each domain is checked automatically at{' '}
        <span className="font-mono">/healthz</span>. If your server stays
        unreachable for {gracePeriodLabel}, the domain and its DNS record are
        removed and the name becomes available to someone else.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        {rows.map(({ domain, health }) => {
          return (
            <DomainRow
              createdAtLabel={dateFormatter.format(new Date(domain.createdAt))}
              domain={domain}
              health={health}
              isBusy={isBusy}
              isDeleting={pendingDeleteIds.has(domain.id)}
              isUpdating={pendingUpdateIds.has(domain.id)}
              key={domain.id}
              onDelete={onDelete}
              onUpdateSettings={onUpdateSettings}
            />
          );
        })}
      </div>
    </section>
  );
};

export const DomainList = memo(DomainListComponent);
